import React, { useMemo } from 'react';
import './Radar.css';

const RADAR_RINGS = [120, 220, 320, 420];

const DeviceNode = ({ name, id, isMe, role }) => {
    // Generate a pseudo-random position based on the ID string
    const { top, left, scale } = useMemo(() => {
        if (isMe) return { top: 'unset', left: '50%', scale: 1 };
        
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            hash = id.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Random angle (0-360) and ring (1, 2, or 3)
        const angle = (Math.abs(hash) % 270) + 45; // Avoid bottom area
        const ringIdx = (Math.abs(hash) % 3) + 1; // Rings 1, 2, or 3
        const radius = RADAR_RINGS[ringIdx] / 2;
        
        const rad = (angle * Math.PI) / 180;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        
        return {
            top: `calc(50% - ${y}px)`,
            left: `calc(50% + ${x}px)`,
            scale: 0.9 + (Math.abs(hash) % 3) * 0.1
        };
    }, [id, isMe]);

    const isMobile = name.toLowerCase().includes('mobile');
    const avatar = role === 'sender' ? '🖥️' : (isMobile ? '📱' : '💻');
    
    // Gradient based on role or device
    const bg = role === 'sender' 
        ? 'linear-gradient(135deg,#1a2a1f,#0d1f16)' 
        : 'linear-gradient(135deg,#1a1a2e,#0d0d1f)';

    return (
        <div 
            className={`device-node ${isMe ? 'host' : 'guest found'}`}
            style={{ top, left, transform: isMe ? 'translateX(-50%)' : `translate(-50%, -50%) scale(${scale})` }}
        >
            <div className="device-avatar" style={{ background: bg }}>
                {avatar}
            </div>
            <div className="device-label">
                {isMe ? "You" : name}
            </div>
        </div>
    );
};

const Radar = ({ peers = {}, myId, pin }) => {
    // Convert peers object to array and filter out empty or old ones
    const peerList = Object.entries(peers).map(([id, data]) => ({
        id,
        ...data
    }));

    return (
        <div className="radar-main">
            <div className="radar-visual">
                <div className="radar-ring"></div>
                <div className="radar-ring"></div>
                <div className="radar-ring"></div>
                <div className="radar-ring"></div>
                <div className="radar-sweep"></div>
                <div className="radar-center-dot"></div>

                {/* Always render 'Me' node */}
                {pin && <DeviceNode id={myId} name="Me" isMe={true} role={peers[myId]?.role || "receiver"} />}

                {/* Render other peers */}
                {peerList.filter(p => p.id !== myId).map(p => (
                    <DeviceNode key={p.id} {...p} isMe={false} />
                ))}
            </div>
            
            {!pin && (
                <div className="radar-tip" style={{ position: 'absolute', bottom: '32px', textAlign: 'center', opacity: 0.5, fontSize: '13px' }}>
                    Join a session to start discovery
                </div>
            )}
        </div>
    );
};

export default Radar;
